/** The changeset failures the editor handles in place, recognised by shape so the
 *  record core does not depend on one record kind's error classes (the booking API's
 *  `DriftError` / `ChangesetValidationError` both fit).
 *
 *  - drift (409): `drifted[]` — the fields whose stored value moved; nothing was written.
 *  - validation (400): `errors` keyed `"<entity>:<id>:<field or action>"`,
 *    `"options.<key>"`, `"events.<key>"`, `"description"` or `"changes"`.
 *  - refused: `refused: {message, links}` — a change the server will not take from this
 *    editor at all, and where it is made instead (an easy tenant's launch policy → Go live in
 *    its easy setup). A record kind's apply throws it; it carries `message` too, so anything
 *    that only reads the message still has the words.
 */
import type { RecordDriftedField, RecordFieldError } from './types';

export interface DriftFailure {
  drifted: RecordDriftedField[];
}

export interface ValidationFailure {
  code: string;
  message: string;
  errors: Record<string, RecordFieldError[]>;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export function asDriftFailure(e: unknown): DriftFailure | null {
  if (!isObject(e) || !Array.isArray(e.drifted)) return null;
  return { drifted: e.drifted as RecordDriftedField[] };
}

export function asValidationFailure(e: unknown): ValidationFailure | null {
  if (!isObject(e) || typeof e.code !== 'string' || !isObject(e.errors) || Array.isArray(e.errors)) {
    return null;
  }
  return {
    code: e.code,
    message: typeof e.message === 'string' ? e.message : 'The changes were refused.',
    errors: e.errors as Record<string, RecordFieldError[]>,
  };
}

export interface RefusalLink {
  to: string;
  label: string;
}

export interface RefusalFailure {
  message: string;
  links: RefusalLink[];
}

/** The error a record kind's apply throws for a refused save (see above). */
export function refusalFailure(message: string, links: readonly RefusalLink[]): { message: string; refused: RefusalFailure } {
  return { message, refused: { message, links: [...links] } };
}

function isRefusalLink(v: unknown): v is RefusalLink {
  return isObject(v) && typeof v.to === 'string' && typeof v.label === 'string';
}

export function asRefusalFailure(e: unknown): RefusalFailure | null {
  if (!isObject(e) || !isObject(e.refused)) return null;
  const { message, links } = e.refused;
  if (typeof message !== 'string' || !message) return null;
  return { message, links: Array.isArray(links) ? links.filter(isRefusalLink) : [] };
}

/** A message for anything else (network, 500, permission). */
export function failureMessage(e: unknown): string {
  if (isObject(e) && typeof e.message === 'string' && e.message) return e.message;
  return 'The save failed.';
}

/** Is this 400 key a change (`<entity>:<id>:<name>`) rather than a form-level key? A
 *  negative id is a row the draft creates (`alias:-1:alias.custom_id`). */
export function isChangeErrorKey(key: string): boolean {
  return /^[a-z_]+:-?\d+:.+$/.test(key);
}
